import { useTheme } from '@emotion/react'
import { Box, Button, Divider, Typography } from '@mui/material'
import { IconInfoCircleFilled } from '@tabler/icons-react'
import { useCallback, useEffect, useState } from 'react'
import { Dialog } from '~/components/Dialog'
import { useFormContext, useWatch } from 'react-hook-form'
import { EMonths, transformKWH, transformMoney, translateMonth } from '~/utils'
import { DocumentationForm } from '~/pages/ProposalCreate/components/Documentation'
import { EProposalStatus, useProposalUpdateMutation } from '~/graphql/types'
import { useProposalContext } from '~/contexts'
import { Input } from '../Form/Input'
import { toast } from 'react-toastify'

type MonthlyConsumption = Record<string, number | null>

export type ProposalAverageConsumptionDialogProps = {
  isVisible: boolean;
  toggleModal: () => void;
};

export const monthsLabel = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december'
]

const getFilledIndexes = (obj: MonthlyConsumption) =>
  monthsLabel.map((month, i) => obj[month] !== null ? i : -1).filter(i => i !== -1)

const areIndexesConsecutive = (indexes: number[]) => {
  if (indexes.length <= 1) return true
  return indexes
    .slice(1)
    .every((idx, i) => Math.abs(idx - indexes[i]) === 1)
}

const calculateAverage = (updatedConsumptions: MonthlyConsumption) => {
  const filledIndexes = getFilledIndexes(updatedConsumptions)
  if (filledIndexes.length === 0) return 0
  if (!areIndexesConsecutive(filledIndexes)) return 0
  const total = filledIndexes.reduce((sum, idx) => sum + (updatedConsumptions[monthsLabel[idx]] || 0), 0)
  return total / filledIndexes.length
}

const findSequenceError = (consumptions: MonthlyConsumption): string | null => {
  const filledIndexes = getFilledIndexes(consumptions)
  if (filledIndexes.length <= 1) return null

  const gapIndex = filledIndexes
    .slice(1)
    .find((idx, i) => idx - filledIndexes[i] > 1)

  if (gapIndex !== undefined) {
    const prevIndex = filledIndexes[filledIndexes.indexOf(gapIndex) - 1]
    return monthsLabel[prevIndex + 1]
  }

  return null
}

export const ProposalAverageConsumptionDialog: React.FC<ProposalAverageConsumptionDialogProps> = ({ isVisible, toggleModal }) => {
  const { proposal, setProposal } = useProposalContext()
  const [kWhAverage, setKWhAverage] = useState(proposal?.energyBillInfo?.avarageConsumption || 0)
  const form = useFormContext<DocumentationForm>()
  const consumptions = useWatch(
    { control: form.control, name: 'monthlyConsumption' }
  )
  const [sequenceError, setSequenceError] = useState<string | null>(null)
  const [proposalUpdate, { loading: proposalUpdateIsLoading }] = useProposalUpdateMutation({
    onCompleted(data) {
      if (proposal) {
        const formValue = form.getValues()
        setProposal(
          {
            ...proposal,
            // monthlyConsumption: { ...data.proposalUpdate.monthlyConsumption },
            monthlyConsumption: {
              january: data.proposalUpdate.monthlyConsumption?.january,
              february: data.proposalUpdate.monthlyConsumption?.february,
              march: data.proposalUpdate.monthlyConsumption?.march,
              april: data.proposalUpdate.monthlyConsumption?.april,
              may: data.proposalUpdate.monthlyConsumption?.may,
              june: data.proposalUpdate.monthlyConsumption?.june,
              july: data.proposalUpdate.monthlyConsumption?.july,
              august: data.proposalUpdate.monthlyConsumption?.august,
              september: data.proposalUpdate.monthlyConsumption?.september,
              october: data.proposalUpdate.monthlyConsumption?.october,
              november: data.proposalUpdate.monthlyConsumption?.november,
              december: data.proposalUpdate.monthlyConsumption?.december,
            },
            energyBillInfo: {
              ...proposal.energyBillInfo,
              ...formValue,
              AmountCents: transformMoney(Number(formValue.AmountCents), 'toCents') || 0,
              distributionTaxes: transformMoney(Number(formValue.distributionTaxes), 'toCents') || 0,
              energyTaxes: transformMoney(Number(formValue.energyTaxes), 'toCents') || 0,
              tusdValue: transformMoney(Number(formValue.tusdValue), 'toCents') || 0,
              address: {
                city: formValue.address?.city || '',
                state: formValue.address?.state || '',
                street: formValue.address?.street || '',
                number: formValue.address?.number || '',
                zipcode: formValue.address?.zipcode || '',
                complement: formValue.address?.complement || '',
                neighborhood: formValue.address?.neighborhood || '',
                country: formValue.address?.country || ''
              },

              // AmountCents: formValue.AmountCents,
              // clientNumber: formValue.clientNumber,
              // consumerClassification: formValue.consumerClassification,
              // dealershipId: formValue.dealershipId,
              // distributionTaxes: formValue.distributionTaxes,
              // energyTaxes: formValue.energyTaxes,
              // installationNumber: formValue.installationNumber,
              // invoiceDueDay: formValue.invoiceDueDay,
              // tariffClassification: formValue.tariffClassification,
              // tension: formValue.tension,
              // tusdValue: formValue.tusdValue,
              energyBillFile: proposal.energyBillInfo?.energyBillFile,
              avarageConsumption: Math.round(kWhAverage)
            }
          }
        )
        form.setValue('monthlyConsumption', data.proposalUpdate.monthlyConsumption || {})
        form.setValue('avarageConsumption', String(Math.round(kWhAverage)))
        toast.success('Média de consumo registrada!')
        toggleModal()
      }
    }
  })
  const theme = useTheme()

  useEffect(() => {
    setSequenceError(findSequenceError(consumptions || {}))
    setKWhAverage(calculateAverage(consumptions || {}))
  }, [consumptions])

  const isMonthEnabled = (index: number, monthConsumption: MonthlyConsumption) => {
    const filledIndexes = getFilledIndexes(monthConsumption)
    if (filledIndexes.length === 0) return true
    if (monthConsumption[monthsLabel[index]] != null) return true
    if (filledIndexes.includes(index - 1) || filledIndexes.includes(index + 1)) return true
    return false
  }

  const handleSaveAverage = useCallback(() => {
    if (proposal) {
      proposalUpdate({
        variables: {
          params: {
            _id: proposal._id,
            monthlyConsumption: {
              january: consumptions?.january,
              february: consumptions?.february,
              march: consumptions?.march,
              april: consumptions?.april,
              may: consumptions?.may,
              june: consumptions?.june,
              july: consumptions?.july,
              august: consumptions?.august,
              september: consumptions?.september,
              october: consumptions?.october,
              november: consumptions?.november,
              december: consumptions?.december,
            }
          }
        }
      })
    }

  }, [proposal, consumptions])

  const isEditableByStatus =
    proposal?.status !== EProposalStatus.finalized &&
    proposal?.status !== EProposalStatus.formalization

  useEffect(() => {
    if (proposal) {
      form.setValue('monthlyConsumption', proposal.monthlyConsumption || {})
    }
  }, [])

  return (
    <Dialog
      isVisible={isVisible}
      title='Média de consumo'
      onClose={toggleModal}
      minWidth={700}
      titleIcon={<IconInfoCircleFilled style={{ color: theme.palette.info.dark }} size={32} />}
    >
      <Divider sx={{ paddingTop: '1rem' }} />
      <Box sx={{ display: 'flex', flexFlow: 'column', gap: '1rem', padding: '2rem 0 1rem 0' }}>
        {isEditableByStatus && <Typography>Preencha os campos para ter uma média de consumo.</Typography>}

        <Box display='flex' flexDirection='column' alignItems='flex-start' gap='8px'>
          {(isEditableByStatus
            ? monthsLabel
            : monthsLabel.filter(month => !!consumptions?.[month as EMonths])
          ).map((month, index) => (
            <Box key={index} sx={{ width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  borderRadius: '8px 0 0 8px',
                  paddingTop: '8px',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fafafa',
                    color: '#999999',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '96px',
                    paddingY: '15px',
                    borderRadius: 'inherit',
                    position: 'relative',
                    right: '-6px',
                    zIndex: 1,
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    borderRight: 'none',
                  }}
                >
                  {translateMonth(month as EMonths)}
                </Box>
                <Input
                  mask='kwh'
                  name={`monthlyConsumption.${month}`}
                  label='Média de consumo'
                  icons={{ end: { element: <span>kWh</span> } }}
                  showYupMessage={false}
                  disabled={!isMonthEnabled(index, consumptions || {}) || !isEditableByStatus}
                />
              </Box>
              {sequenceError === month && (
                <Typography color='error' variant='caption' sx={{ ml: 2 }}>
                  O mês precisa ser consecutivo a outro já preenchido.
                </Typography>
              )}

              {form.formState.errors.monthlyConsumption?.[month as EMonths]?.message && (
                <Typography color='error' variant='caption' sx={{ ml: 2 }}>
                  {form.formState.errors.monthlyConsumption[month as EMonths]?.message as string}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
        <Box display='flex' flexDirection='row' alignItems='center' justifyContent='flex-end' gap='8px' sx={{ paddingBottom: '0.5rem', paddingTop: '0.25rem' }}>
          <Typography color='#999999'>Média de consumo mensal: </Typography>
          <Typography color='#561885' sx={{ fontSize: '24px' }}>{transformKWH(String(Math.round(kWhAverage)))} kWh</Typography>
        </Box>
        <Divider />
        {isEditableByStatus ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1rem' }}>
            <Button disabled={false} onClick={toggleModal} color='secondary'>
              Cancelar
            </Button>
            <Button
              disabled={
                sequenceError !== null ||
                !areIndexesConsecutive(getFilledIndexes(consumptions || {})) ||
                getFilledIndexes(consumptions || {}).length === 0 || proposalUpdateIsLoading || !!Object.values(form.formState.errors.monthlyConsumption || {}).length
              }
              onClick={() => handleSaveAverage()}
              type='submit'
              sx={{ background: '#3BA1FF', color: '#ffffff', '&:hover': { background: '#3ba0ffe6', '&:focus': { background: '#3ba0ffe6' } } }}
            >
              Salvar média
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1rem' }}>

            <Button
              onClick={() => toggleModal()}
              sx={{ background: '#3BA1FF', color: '#ffffff', '&:hover': { background: '#3ba0ffe6', '&:focus': { background: '#3ba0ffe6' } } }}
            >
              Concluir
            </Button>
          </Box>
        )}

      </Box>

    </Dialog >
  )
}
